// changeTable — VQE-Edit edit-action: swap the relation referenced by a FROM
// item (by 0-based index) via SR-Edit's `change-from-item-table` op. Builds
// the replacement text (new relation ref + preserved alias) and delegates to
// `commitEdit`. Only valid for `kind: 'relation'` FROM items — derived-table,
// cte-ref, and function-call items are unchanged (they have complex structure
// that a table-picker can't represent as a simple relation reference).
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — change FROM item table.

import type { Table } from '../../../../sql-resolver/types/schema';
import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import { commitEdit, type CommitEditCtx, type CommitOutcome } from '../commitEdit';
import { formatCatalogTableRef } from './fromItemHelpers';

export type ChangeTableOptions = {
  /**
   * Optional structure used for bounds-checking and alias preservation.
   * When supplied, the function validates `index`, ensures the target is a
   * `relation` kind, and appends the existing alias (if any) to the new text.
   * When omitted, the edit fires without validation or alias injection —
   * callers are responsible for passing the correct pre-formatted text via a
   * future `textOverride` mechanism.
   */
  readonly structure?: QueryStructure;
};

export async function changeTable(
  index: number,
  table: Table,
  ctx: CommitEditCtx,
  options: ChangeTableOptions = {},
): Promise<CommitOutcome> {
  if (!Number.isInteger(index) || index < 0) {
    throw new Error(`changeTable: index must be a non-negative integer (got ${index})`);
  }

  let text = formatCatalogTableRef(table, ctx.service.engineId);

  if (options.structure) {
    const items = options.structure.fromItems ?? [];
    if (items.length === 0) {
      throw new Error('changeTable: FROM list is empty');
    }
    if (index >= items.length) {
      throw new Error(
        `changeTable: index ${index} out of range (FROM list has ${items.length} item(s))`,
      );
    }
    const item = items[index];
    if (item.target.kind !== 'relation') {
      throw new Error(
        `changeTable: FROM item at index ${index} is not a plain relation (kind: ${item.target.kind}) — only relation items can be changed via the table picker`,
      );
    }
    // Preserve the existing alias so dependent column references still resolve.
    // Slice the alias token verbatim from the source buffer (range offsets are
    // buffer-absolute, same coordinate space as `item.range`) so its original
    // quoting survives — re-emitting the parsed `alias` value drops quotes and
    // breaks case-sensitive (`"F"`) or space-containing aliases. Falls back to
    // the bare value when the source/range is unavailable.
    if (item.alias) {
      const source = ctx.service.getParsedBuffer(ctx.documentId)?.parsedDocument.source;
      const { startOffset, endOffset } = item.alias.range;
      const rawAlias =
        source && endOffset > startOffset && endOffset <= source.length
          ? source.slice(startOffset, endOffset)
          : item.alias.alias;
      text = `${text} ${rawAlias}`;
    }
  }

  // Index/kind pre-validation above still THROWS before commitEdit (unchanged);
  // the commit itself no longer rejects — forward its `CommitOutcome` so the
  // caller only fires success analytics on `applied`.
  return commitEdit({ op: 'change-from-item-table', index, text }, ctx);
}
